import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHead, Card } from "@/pages/app/AppShell";
import { api, errMsg } from "@/lib/api";

const PRESETS = ["Naming Rights", "Main Stage", "Runway", "VIP Lounge", "Step & Repeat", "Digital Campaign", "Influencer Content", "Product Placement"];

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ asset: "", total: 1, unit_price: "" });
  const load = () => api.get("/inventory").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async (asset = f.asset, total = f.total) => {
    try {
      await api.post("/inventory", { asset, total: Number(total) || 0, unit_price: Number(f.unit_price) || 0 });
      setF({ asset: "", total: 1, unit_price: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (i, body) => {
    try { await api.patch(`/inventory/${i.id}`, body); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const missing = PRESETS.filter((p) => !items.some((i) => i.asset.toLowerCase() === p.toLowerCase()));
  return (
    <div data-testid="inventory-page">
      <PageHead kicker="Sponsorship inventory" title="Everything you can sell" />
      <Card className="p-6">
        <div className="grid grid-cols-[1fr_5rem] sm:grid-cols-[1fr_7rem_9rem_auto] gap-3 items-end">
          <input className="field" placeholder="Asset (e.g. VIP Lounge)" value={f.asset} onChange={(e) => setF({ ...f, asset: e.target.value })} data-testid="inventory-asset-input" />
          <input className="field" type="number" min={0} value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} aria-label="Available" data-testid="inventory-total-input" />
          <input className="field col-span-2 sm:col-span-1" type="number" min={0} placeholder="Unit price $" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid="inventory-price-input" />
          <button className="btn-gold col-span-2 sm:col-span-1" disabled={!f.asset.trim()} onClick={() => add()} data-testid="inventory-add-button"><Plus className="h-4 w-4" />Add</button>
        </div>
        {missing.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="inventory-presets">
            <span className="text-xs text-slate-400 self-center mr-1">Quick add:</span>
            {missing.map((p) => <button key={p} onClick={() => add(p, 1)} className="rounded-full bg-[#2A1240]/5 px-3 py-1 text-xs text-slate-200 hover:bg-[#2A1240]/10" data-testid={`inventory-preset-${p.toLowerCase().replace(/\W+/g, "-")}`}>+ {p}</button>)}
          </div>
        )}
      </Card>
      <Card className="mt-6 overflow-x-auto" data-testid="inventory-table">
        <table className="w-full min-w-[560px] text-sm">
          <thead><tr className="text-[11px] uppercase tracking-[0.18em] text-slate-400"><th className="p-4 text-left font-normal">Asset</th><th className="p-4 text-center font-normal">Total</th><th className="p-4 text-center font-normal">Reserved</th><th className="p-4 text-center font-normal">Available</th><th className="p-4 text-right font-normal">Unit price</th><th className="p-4" /></tr></thead>
          <tbody>
            {!items.length && <tr><td colSpan={6} className="p-8 text-center text-slate-400" data-testid="inventory-empty">No assets yet — add what your event or platform can offer partners.</td></tr>}
            {items.map((i) => (
              <tr key={i.id} className="border-t border-[#2A1240]/10" data-testid={`inventory-item-${i.id}`}>
                <td className="p-4 text-[#2A1240]">{i.asset}</td>
                <td className="p-4 text-center"><input type="number" min={i.reserved} defaultValue={i.total} onBlur={(e) => Number(e.target.value) !== i.total && patch(i, { total: Number(e.target.value) })} className="w-16 rounded bg-[#FBE9F3] border border-[#2A1240]/10 px-2 py-1 text-center" data-testid={`inventory-total-${i.id}`} /></td>
                <td className="p-4 text-center font-mono text-slate-300">{i.reserved}</td>
                <td className="p-4 text-center"><span className={`font-mono ${i.available ? "text-[#8E2BC4]" : "text-slate-500"}`} data-testid={`inventory-available-${i.id}`}>{i.available}</span></td>
                <td className="p-4 text-right font-mono text-slate-300">{i.unit_price ? `$${Number(i.unit_price).toLocaleString()}` : "—"}</td>
                <td className="p-4 text-right"><button className="text-slate-400 hover:text-red-300" onClick={() => api.delete(`/inventory/${i.id}`).then(load)} aria-label="Delete" data-testid={`inventory-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="mt-4 text-xs text-slate-400">Accepting a package request on a brief reserves its assets automatically, so you never double-sell.</p>
    </div>
  );
}
